import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Plus,
    Search,
    Filter,
    Edit,
    Trash2,
    Eye,
    Package,
    TrendingUp,
    Store,
    Archive,
    Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const ProductList = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const res = await api.get('/products');
            if (res.data.success) {
                setProducts(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Failed to load product catalog');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;
        try {
            await api.delete(`/products/${id}`);
            toast.success('Product deleted successfully');
            fetchProducts();
        } catch (error) {
            toast.error('Failed to delete product');
        }
    };

    const stats = [
        { label: 'Total Products', value: products.length, icon: Store, color: 'text-primary' },
        { label: 'Live Store', value: products.filter(p => p.status === 'published').length, icon: TrendingUp, color: 'text-emerald-500' },
        { label: 'Archived', value: products.filter(p => p.status === 'archived').length, icon: Archive, color: 'text-slate-500' },
    ];

    const filteredProducts = products.filter(p =>
        p.product_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.category_id?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6 animate-in fade-in duration-500 pb-10">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-6">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">Inventory</h1>
                    <p className="text-slate-400 mt-1 text-sm">Manage global digital asset registry</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-slate-400 hover:text-white hover:border-violet-500 transition-all cursor-pointer">
                        <Filter size={16} /> <span className="text-sm font-medium">Filter</span>
                    </div>
                    <button
                        onClick={() => navigate('/admin/products/add')}
                        className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold rounded-xl transition-all shadow-lg shadow-violet-600/20 active:scale-[0.98] flex items-center gap-2"
                    >
                        <Plus size={18} strokeWidth={2.5} /> Add Product
                    </button>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-indigo-600 to-blue-700 p-6 rounded-2xl shadow-lg shadow-indigo-900/20 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Store size={80} />
                    </div>
                    <p className="text-indigo-100 text-sm font-medium mb-1">Total Manifest</p>
                    <h3 className="text-3xl font-bold text-white">{products.length}</h3>
                    <div className="mt-4 flex items-center gap-2">
                        <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">All Items</span>
                        <span className="text-indigo-200 text-xs">In registry</span>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-6 rounded-2xl shadow-lg shadow-emerald-900/20 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <TrendingUp size={80} />
                    </div>
                    <p className="text-emerald-100 text-sm font-medium mb-1">Operational Status</p>
                    <h3 className="text-3xl font-bold text-white">{products.filter(p => p.status === 'published').length}</h3>
                    <div className="mt-4 flex items-center gap-2">
                        <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">Live</span>
                        <span className="text-emerald-100 text-xs">Store items</span>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-slate-600 to-slate-700 p-6 rounded-2xl shadow-lg shadow-slate-900/20 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Archive size={80} />
                    </div>
                    <p className="text-slate-200 text-sm font-medium mb-1">Archived Data</p>
                    <h3 className="text-3xl font-bold text-white">{products.filter(p => p.status === 'archived').length}</h3>
                    <div className="mt-4 flex items-center gap-2">
                        <span className="bg-white/10 text-white text-xs px-2 py-0.5 rounded-full">Hidden</span>
                        <span className="text-slate-300 text-xs">Inactive</span>
                    </div>
                </div>
            </div>

            {/* Search and Table Section */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                <div className="p-4 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900/50">
                    <div className="relative group max-w-sm w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-violet-400 transition-colors" size={18} />
                        <input
                            type="text"
                            placeholder="Search manifest records..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-10 pr-4 py-2 text-sm font-medium text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/50 transition-all"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                            <Loader2 className="animate-spin mb-4 text-violet-500" size={32} />
                            <p className="text-xs font-semibold uppercase tracking-wider">Loading Data...</p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-800 bg-slate-900/50">
                                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Asset Identity</th>
                                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Taxonomy</th>
                                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Valuation</th>
                                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Metrics</th>
                                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Status</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-400">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800">
                                {filteredProducts.length > 0 ? filteredProducts.map((item, idx) => (
                                    <motion.tr
                                        key={item._id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: idx * 0.02 }}
                                        className="group hover:bg-slate-800/50 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-blue-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 group-hover:bg-indigo-500 group-hover:text-white transition-all shadow-sm">
                                                    <Package size={18} />
                                                </div>
                                                <div>
                                                    <p className="text-white font-bold text-sm group-hover:text-indigo-400 transition-colors">{item.product_title}</p>
                                                    <p className="text-slate-500 text-xs mt-0.5">{item.product_type} • V{item.version}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="bg-slate-800 border border-slate-700 text-slate-300 px-3 py-1 rounded-full text-xs font-medium">
                                                {item.category_id?.name || 'Uncategorized'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-white font-bold text-sm">₹{item.regular_price}</span>
                                                {item.sale_price && <span className="text-xs text-slate-500 line-through">₹{item.sale_price}</span>}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <span className="text-white font-bold text-sm">{item.total_sales || 0}</span>
                                                <span className="text-xs text-slate-500">Sales</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${item.status === 'published' ? 'bg-emerald-500 animate-pulse' : 'bg-slate-500'}`}></div>
                                                <span className={`text-xs font-medium ${item.status === 'published' ? 'text-emerald-400' : 'text-slate-500'}`}>
                                                    {item.status === 'published' ? 'Published' : item.status}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => window.open(`/products/${item.slug}`, '_blank')}
                                                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-all"
                                                    title="View"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/admin/products/edit/${item._id}`)}
                                                    className="p-2 text-slate-400 hover:text-white hover:bg-violet-500 rounded-lg transition-all"
                                                    title="Edit"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                <button onClick={() => handleDelete(item._id)} className="p-2 text-slate-400 hover:text-white hover:bg-rose-500 rounded-lg transition-all" title="Delete"><Trash2 size={16} /></button>
                                            </div>
                                        </td>
                                    </motion.tr>
                                )) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center gap-4">
                                                <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center">
                                                    <Archive className="text-slate-600" size={32} />
                                                </div>
                                                <p className="text-slate-500 font-medium text-sm">No products found in registry</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between text-slate-500 text-xs font-medium bg-slate-900/50">
                    <p>Total Records: {filteredProducts.length}</p>
                    <p>Secure Encryption: AES-256</p>
                </div>
            </div>
        </div>
    );
};

export default ProductList;
